import { z } from 'zod'

import { EssayProblemSlotSchema } from '~/@entities/Problem'
import { StudentIdSchema } from '~/@entities/Student'
import { StudentWorksheetSchema } from '~/@entities/StudentWorksheet'
import { WorksheetProblemSchema } from '~/@entities/Worksheet'

/***************** 학생 채점 진행 상태 *****************/
// 저장 컬럼이 아니라 서버가 문항 채점 행을 모아 내는 값이다. 채점 주체(AI·수동)는 담지 않는다
export const ESSAY_STUDENT_STATUS = [
  'NOT_SUBMITTED',
  'AI_SCORING',
  'NEEDS_MANUAL',
  'COMPLETED',
] as const

/***************** 문항 채점 상태 *****************/
// NOT_SUBMITTED 는 채점 행이 없다는 뜻이다. 응답은 행을 빼기도, NOT_SUBMITTED 행을 싣기도 한다
export const ESSAY_SCORING_STATUS = [
  'NOT_SUBMITTED',
  'IN_PROGRESS',
  'SCORED',
  'DROP',
  'FAILED',
  'AI_SKIPPED',
] as const

/***************** 보고서 본문 구성 *****************/
export const ESSAY_REPORT_BODY_COMPOSITION = [
  'SUMMARY',
  'FEEDBACK_SCORE',
  'PROBLEM_FEEDBACK_SCORE',
] as const

/***************** 보고서 총평 방식 *****************/
// 개인 보고서는 DIRECT·NONE, 그룹 보고서는 AI_GENERATED·NONE 만 받는다(요청 타입이 경로별로 좁힌다)
export const ESSAY_REPORT_COMMENT_MODE = ['AI_GENERATED', 'DIRECT', 'NONE'] as const

export const EssayStudentStatusSchema = z.enum(ESSAY_STUDENT_STATUS)
export type EssayStudentStatus = z.infer<typeof EssayStudentStatusSchema>

export const EssayScoringStatusSchema = z.enum(ESSAY_SCORING_STATUS)
export type EssayScoringStatus = z.infer<typeof EssayScoringStatusSchema>

export const EssayReportBodyCompositionSchema = z.enum(ESSAY_REPORT_BODY_COMPOSITION)
export type EssayReportBodyComposition = z.infer<typeof EssayReportBodyCompositionSchema>

export const EssayReportCommentModeSchema = z.enum(ESSAY_REPORT_COMMENT_MODE)
export type EssayReportCommentMode = z.infer<typeof EssayReportCommentModeSchema>

const _LocalDateTimeSchema = z.iso.datetime({ local: true })

/***************** 학생 *****************/
/** 채점 화면의 학생 한 명 — 학년·반 단위로 조회한다 */
export const EssayScoringStudentSchema = z.object({
  studentWorksheetId: StudentWorksheetSchema.shape.id,
  studentId: StudentIdSchema,
  studentName: z.string(),
  status: EssayStudentStatusSchema,
  // 문항 점수의 합. 전 문항이 채점되기 전에는 null
  score: z.number().nullable(),
  // null 이면 미공개. 공개는 문항이 아니라 학습지 단위다
  scoringOpenDatetime: _LocalDateTimeSchema.nullable(),
})
export type EssayScoringStudent = z.infer<typeof EssayScoringStudentSchema>

/***************** 문항 채점 *****************/
/** 슬롯 하나의 답안과 판정 */
export const EssaySubScoringSchema = z.object({
  tag: EssayProblemSlotSchema.shape.tag,
  // 미작성 슬롯은 null
  handwritingUrl: z.string().nullable(),
  // 빈칸 정오. null 은 미채점·인식 불가('확인 필요'). 단계형·서술형은 항상 null
  result: z.boolean().nullable(),
  // OCR 인식 결과(빈칸형만). 인식 불가는 null
  recognizedAnswer: z.string().nullable(),
})
export type EssaySubScoring = z.infer<typeof EssaySubScoringSchema>

/** 학생 한 명의 문항 하나에 대한 채점. 학생은 조회 경로의 studentWorksheetId 가 가리킨다 */
export const EssayProblemScoringSchema = z.object({
  worksheetProblemId: WorksheetProblemSchema.shape.id,
  status: EssayScoringStatusSchema,
  // AI·교사가 함께 쓰는 한 칸(AI 가채점 값을 교사가 이어서 고친다). null 은 미채점
  score: z.number().nullable(),
  // AI·교사가 함께 쓰는 한 칸. 빈칸형은 항상 null
  feedback: z.string().nullable(),
  // 수기 채점하거나 AI 결과를 고쳐 저장하면 true
  teacherScored: z.boolean(),
  // 미제출이면 빈 배열
  subScorings: z.array(EssaySubScoringSchema),
  // 문제 이미지 위 자유 필기. 없으면 null
  solutionHandwritingUrl: z.string().nullable(),
})
export type EssayProblemScoring = z.infer<typeof EssayProblemScoringSchema>

/** 학생 한 명의 채점 전체. 학생·학부모는 공개된 학습지만 받는다(미공개 403) */
export const EssayScoringDetailSchema = z.object({
  // 문항이 아니라 학습지 단위 값이다. 제출 전에는 예정, 제출 후에는 사실을 담는다
  manualScoring: z.boolean(),
  // 출제 순서
  scorings: z.array(EssayProblemScoringSchema),
})
export type EssayScoringDetail = z.infer<typeof EssayScoringDetailSchema>

/** 문항 채점을 저장한 결과 */
export const EssayScoringSaveResultSchema = z.object({
  worksheetProblemId: WorksheetProblemSchema.shape.id,
  // 빈칸형을 일부만 판정해 저장하면 SCORED 로 바뀌지 않고 이전 상태를 유지한다
  status: EssayScoringStatusSchema,
  // 빈칸형은 전 칸이 판정됐을 때만 값이 있다
  score: z.number().nullable(),
  maxScore: z.number(),
  // 저장 뒤 다시 계산한 학생 총점
  totalScore: z.number(),
})
export type EssayScoringSaveResult = z.infer<typeof EssayScoringSaveResultSchema>

/***************** 보고서 *****************/
/** 유형(소단원)별 점수 집계. 보고서 막대 그래프와 같은 데이터이고 받은 순서대로 그린다 */
export const EssayReportTypeAnalysisSchema = z.object({
  typeName: z.string(),
  score: z.number(),
  maxScore: z.number(),
  // 획득 비율(0~100)
  percent: z.number(),
  comment: z.string().nullable(),
})
export type EssayReportTypeAnalysis = z.infer<typeof EssayReportTypeAnalysisSchema>

/** 보고서에 넣을 AI 총평 미리보기 */
export const EssayReportCommentSchema = z.object({
  studentWorksheetId: StudentWorksheetSchema.shape.id,
  typeAnalysis: z.array(EssayReportTypeAnalysisSchema),
  overallSummary: z.string(),
})
export type EssayReportComment = z.infer<typeof EssayReportCommentSchema>

/** 내려받을 보고서 파일 */
export const EssayReportFileSchema = z.object({
  presignedUrl: z.string(),
  fileName: z.string(),
})
export type EssayReportFile = z.infer<typeof EssayReportFileSchema>

/** 개인 보고서 생성 결과 */
export const CreatedEssayReportSchema = EssayReportFileSchema.extend({
  // 올림앱 공유를 함께 요청했을 때만 값이 있다. 공유가 실패해도 보고서는 만들어진다
  creverseShare: z
    .object({
      // 'ok' 또는 'fail'. 외부(크레버스) 응답을 그대로 옮긴 값이라 좁히지 않는다
      result: z.string(),
      code: z.string(),
      msg: z.string(),
    })
    .nullable(),
})
export type CreatedEssayReport = z.infer<typeof CreatedEssayReportSchema>
